// Composable für die Live-Score-WebSocket-Verbindung: Verbinden, Nachrichten entpacken/anwenden,
// Reconnect mit exponentiellem Backoff, sauberes Aufräumen beim Unmount.
//
// Geteilt über alle Widget-Instanzen mit derselben wsUrl (Spielplan-Widget + Startseite, auch
// mehrere gleichzeitig) - nur EINE tatsächliche WebSocket-Verbindung pro wsUrl, Updates werden an
// alle registrierten matches-Refs verteilt.
//
// HEARTBEAT/RECONNECT-STRATEGIE (siehe Session-Notizen "Reconnect-Debugging"):
// - Reiner close-Event-basierter Reconnect reicht NICHT: bei Netzwerkwechsel, Backgrounding
//   (Handy-App inaktiv) oder Idle-Timeouts von Proxies/NAT stirbt die Verbindung oft OHNE dass
//   ein close/error-Event ankommt (halb-offene Verbindung, Browser hält den Socket für "offen").
// - Deshalb: Application-Level-Ping alle HEARTBEAT_INTERVALL ms. Kommt innerhalb von
//   PONG_TIMEOUT ms kein Pong zurück, wird die Verbindung als tot betrachtet und aktiv
//   geschlossen -> das triggert die bestehende close-Reconnect-Logik.
// - Zusätzlich: bei Tab-Reaktivierung (visibilitychange) und bei zurückkommendem Netz (online)
//   wird SOFORT geprüft/reconnected, statt auf den nächsten Heartbeat-Zyklus zu warten.
import { ref, reactive } from 'vue'
import { parseWsNachricht, entpackeNachricht, wendeWsUpdateAn, ergebnisAusOddsMatchScores } from '../lib/liveMessage.js'
import { vergleicheRang } from './rangSortierung.js'

const geteilterWsCache = new Map() // wsUrl -> Eintrag (siehe unten)
const reaktivierungsCallbacks = new Set()

const HEARTBEAT_INTERVALL = 20000  // alle 20s ein Ping
const PONG_TIMEOUT = 8000          // wenn nach 8s kein Pong: Verbindung als tot behandeln

// WICHTIG: auf Modul-Ebene, NICHT innerhalb von useLiveScores() - sonst bekäme jede
// Widget-Instanz (tm-spielplan, tm-rangliste, ...) ihr eigenes, unabhängiges Objekt, obwohl die
// WS-Nachricht ja nur einmal ankommt (geteilte Verbindung). Analog zum useFavoriten.js-Fix.
const rankingGesamt = ref([])           // company-weites Ranking
const rankingsProRunde = reactive({})   // { [roundID]: RankingLiveNew[] }
const aenderungenProRunde = reactive({})   // { "roundID_registrationID": { zeit, rangAlt, punkteAlt } }
const tickerEintraege = ref([])
const letzteStaende = new Map()   // idDB -> "h:a"
let tickerId = 0
const liveSpiele = reactive({})   // idDB -> aktueller Stand des Spiels
const meineTipps = ref([])
const meineSpiele = ref([])
const szenarien = ref({})   // Schlüssel `${roundID}_${matchid}`
const letztesUpdate = ref(null) 
const features = ref([])
const spielerTipps = ref({})   // key: `${roundID}_${registrationID}` -> Zeilen
const szenarienTop = ref({})      // Schlüssel = roundID (als Text), Wert = { platzJetzt, gleichJetzt, anzahl, chancen[], gefahren[] }

function nachReaktivierung() {
  reaktivierungsCallbacks.forEach(fn => {
    try { fn() } catch (e) { console.warn('Reaktivierungs-Callback fehlgeschlagen:', e) }
  })
}

function mappeTipp(t) {
  return {
    id: `${t.roundID}_${t.matchid}_${t.tippkind}_${t.tippcaption}`,
    roundID: t.roundID,
    matchId: tippMatchId({ tipp: t }),
    tipp: t.tippcaption ?? '',
    quote: t.quote ?? null,
    richtig: !!t.tippOK,
    auszahlung: Number(t.auszahlung ?? 0),
    // Spielinfo vom Server (für Spiele, die nicht in liveSpiele stehen, z. B. abgeschlossene)
    spielInfo: t.heim
      ? {
          heim: t.heim,
          auswaerts: t.auswaerts,
          heimtore: t.heimtore ?? null,
          heimLogo: t.heimLogo ?? null,
          auswLogo: t.auswLogo ?? null,
          austore: t.austore ?? null,
          minute: t.minute ?? null,
          extra: t.spielUhrExtra ?? null,
          kurz: t.statusKurz ?? null,
          completed: !!t.completed
        }
      : null
  }
}

function tippMatchId(t) {
  try { return Number(JSON.parse(t.tipp?.json ?? '{}').factors?.matchID) || null } catch { return null }
}

function pflegeTipps(daten) {
  for (const score of daten.scores ?? []) {
    const oms = score.oddsMatchScores
    if (!oms || oms.idDB == null) continue
    const st = ergebnisAusOddsMatchScores(oms)
    const id = Number(oms.idDB)
    // Nachricht ohne Ergebnis darf einen bereits bekannten Stand nicht löschen
    if (st == null && liveSpiele[id]?.heimtore != null && !oms.completed) continue
    liveSpiele[id] = {
      heim: teamName(oms.homeTeam),
      auswaerts: teamName(oms.awayTeam),
      heimtore: st?.heimtore ?? null,
      austore: st?.austore ?? null,
      minute: oms.status?.elapsed ?? null,
      extra: oms.status?.extra ?? null,
      kurz: oms.status?.shortS ?? null,
      completed: !!oms.completed,
      matchid: oms.id != null ? String(oms.id) : null
    }
  }

  if (Array.isArray(daten['meine-spiele'])) {

    //console.log('[ms]', daten['meine-spiele'].map(x => x.matchid + ':' + x.completed + ':' + x.statusKurz).join(' '))

    meineSpiele.value = daten['meine-spiele'].map(t => ({
      id: `${t.roundID}_${t.matchid}_${t.tippkind}_${t.tippcaption}`,
      roundID: t.roundID,
      matchId: tippMatchId({ tipp: t }),
      matchid: t.matchid,
      tipp: t.tippcaption ?? '',
      quote: t.quote ?? null,
      einsatz: Number(t.value ?? 1),
      bonus10: Number(t.bonus10 ?? 0),
      bonus: Number(t.bonus ?? 0),
      richtig: !!t.tippOK,
      auszahlung: Number(t.auszahlung ?? 0),
      anstoss: t.anstoss ?? null,
      heimLogo: t.heimLogo ?? null,
      auswLogo: t.auswLogo ?? null,
      spielInfo: t.heim ? {
        heim: t.heim,
        auswaerts: t.auswaerts,
        heimtore: t.heimtore ?? null,
        austore: t.austore ?? null,
        kurz: t.statusKurz ?? null,
        minute: t.minute ?? null,
        extra: t.spielUhrExtra ?? null,
        completed: !!t.completed
      } : null
    }))
  }

  if (Array.isArray(daten.szenarien)) {
    // Je Runde ersetzen, Einträge anderer Runden bleiben erhalten
    const runden = new Set(daten.szenarien.map(s => String(s.roundID)))
    const neu = {}
    for (const [k, v] of Object.entries(szenarien.value)) {
      if (!runden.has(String(v.roundID))) neu[k] = v
    }
    for (const s of daten.szenarien) neu[`${s.roundID}_${s.matchid}`] = s
    szenarien.value = neu
  }

  // TEST: Tipps eines anderen Spielers
  const st = daten['spieler-tipps']
  if (st && Array.isArray(st.tipps)) {
    spielerTipps.value = {
      ...spielerTipps.value,
      [`${st.roundID}_${st.registrationID}`]: st.tipps.map(mappeTipp)
    }
  }

  /*
  console.log('[spiele-debug]', {
    serverMeineSpiele: daten['meine-spiele']?.length,
    erstesVomServer: daten['meine-spiele']?.[0],
    store: meineSpiele.value.length,
    matchIds: meineSpiele.value.map(t => t.matchId),
    liveSpieleKeys: Object.keys(liveSpiele)
  })*/
}

function teamName(t) {
  return (typeof t === 'string' ? t : t?.name) ?? '?'
}

function pflegeTicker(daten) {
  for (const score of daten.scores ?? []) {
    const oms = score.oddsMatchScores
    if (!oms || oms.idDB == null) continue
    const st = ergebnisAusOddsMatchScores(oms)
    if (!st) continue
    const key = `${st.heimtore}:${st.austore}`
    const alt = letzteStaende.get(oms.idDB)
    letzteStaende.set(oms.idDB, key)
    if (alt === undefined || alt === key) continue   // erster Stand oder unverändert
    tickerEintraege.value = [{
      id: ++tickerId,
      matchId: oms.idDB,
      heim: teamName(oms.homeTeam),
      auswaerts: teamName(oms.awayTeam),
      heimtore: st.heimtore,
      austore: st.austore,
      minute: oms.status?.elapsed ?? null,
      extra: oms.status?.extra ?? null,
      zeit: Date.now()
    }, ...tickerEintraege.value].slice(0, 6)
  }
}

function merkeAenderungen(roundID, alt, neu) {
  if (!Array.isArray(alt) || alt.length === 0) return   // erster Stand: nichts zu vergleichen
  const altMap = new Map(alt.map(e => [e.registrationID, e]))
  const jetzt = Date.now()
  for (const e of neu) {
    const a = altMap.get(e.registrationID)
    if (!a) continue
    const rangAnders = a.ranking !== e.ranking
    const punkteAnders = Math.abs((Number(a.value) || 0) - (Number(e.value) || 0)) > 1e-9
    if (rangAnders || punkteAnders) {
      aenderungenProRunde[`${roundID}_${e.registrationID}`] = {
        zeit: jetzt,
        rangAlt: a.ranking,
        punkteAlt: a.value
      }
    }
  }
}

function wendeWsUpdateAuchAufTeamSpieleAn(daten, liste) {
  wendeWsUpdateAn(daten, liste)
  for (const m of liste) {
    if (m.spieleHeim?.length) wendeWsUpdateAn(daten, m.spieleHeim)
    if (m.spieleAuswaerts?.length) wendeWsUpdateAn(daten, m.spieleAuswaerts)
  }
}

export function useLiveScores(wsUrl, matches) {
  let eintrag = geteilterWsCache.get(wsUrl)
  if (!eintrag) {
    eintrag = {
      wsStatus: ref('verbinde...'),
      rangliste: ref([]),
      socket: null,
      wsRetryDelay: 2000,
      sollReconnecten: true,
      verbindungGestartet: false,
      matchesListen: new Set(),
      rundenGemeldet: new Set(), 
      letzteRankings: null,
      gepuffertePerMatchId: new Map(),
      heartbeatIntervall: null,
      pongTimeout: null,
      reaktivierungsListenerRegistriert: false,
      reconnectTimer: null,
    }
    geteilterWsCache.set(wsUrl, eintrag)
  }
  eintrag.matchesListen.add(matches)

  function verarbeiteNachricht(daten) {

    //console.log('[meine-spiele]', daten['meine-spiele'])

    if (daten && daten.type === 'features' && Array.isArray(daten.features)) {
      features.value = daten.features
      return
    }

    if (daten && (daten.scores || daten.rankings || daten['rounds-live'] || daten['meine-spiele'])) {
      letztesUpdate.value = Date.now()
    }

    pflegeTicker(daten)
    pflegeTipps(daten)

    for (const score of daten.scores ?? []) {
      const idDB = score.oddsMatchScores?.idDB
      if (idDB != null) eintrag.gepuffertePerMatchId.set(Number(idDB), score.oddsMatchScores)
    }

    if (daten['szenarien-top'] && typeof daten['szenarien-top'] === 'object') {
      szenarienTop.value = { ...szenarienTop.value, ...daten['szenarien-top'] }   // je Runde ersetzen, andere bleiben
    }

    if (daten['rankings']) {
      rankingGesamt.value = daten['rankings']
    }
    
    if (Array.isArray(daten['rounds-live'])) {
      //console.log('[DEBUG-1] rounds-live IDs:', daten['rounds-live'].map(r => r.roundID))
      for (const rundenEintrag of daten['rounds-live']) {
        const roundID = rundenEintrag.roundID // NICHT rundenEintrag.round?.id - das Feld heißt direkt roundID
        if (roundID == null) continue

        const neu = (rundenEintrag.rankingsLive ?? []).map(r => {
        const [firstname, ...rest] = String(r.name ?? '').split(' ')
        return {
            registrationID: r.registrationID,
            ranking: r.ranking,
            rankingPrevious: r.rankingPrevious ?? 0,
            value: r.result,
            firstname: firstname ?? '',
            lastname: rest.join(' ')
          }
        })
        merkeAenderungen(roundID, rankingsProRunde[roundID], neu)
        rankingsProRunde[roundID] = neu

        //console.log('[ranking]', roundID, neu.length, neu[0])
        //console.log('[DEBUG-2] rankingsProRunde Keys jetzt:', Object.keys(rankingsProRunde))
      }
    }

    if (Array.isArray(daten.ticker)) {
      tickerEintraege.value = daten.ticker.map(t => ({
        id: `${t.zeit}_${t.matchId}`,
        matchId: t.matchId,
        heim: t.heim,
        auswaerts: t.auswaerts,
        heimtore: t.heimtore,
        austore: t.austore,
        minute: t.minute,
        extra: t.extra ?? null,
        heimTor: !!t.heimTor,
        auswTor: !!t.auswTor,
        zeit: t.zeit ?? null,
        oddsId: t.oddsId ?? null, 
        spieler: t.spieler ?? null, 
        art: t.art ?? null
      }))
    }

    if (Array.isArray(daten.aenderungen)) {
      for (const r of daten.aenderungen) {
        for (const a of r.aenderungen ?? []) {
          aenderungenProRunde[`${r.roundID}_${a.registrationID}`] = {
            zeit: a.zeit,
            rangAlt: a.rangAlt,
            punkteAlt: a.punkteAlt
          }
        }
      }
    }

    if (Array.isArray(daten.rankings)) {
      eintrag.letzteRankings = daten.rankings

      eintrag.rangliste.value = daten.rankings.map(r => ({
        ranking: r.ranking,
        firstname: r.firstname,
        lastname: r.lastname,
        registrationID: r.registrationID,
        value: r.value,
        rankingLive: r.rankingLive,
        rankingPrevious: r.rankingPrevious
      })).sort(vergleicheRang)
    }

    for (const matchesRef of eintrag.matchesListen) {
      wendeWsUpdateAuchAufTeamSpieleAn(daten, matchesRef.value)
      matchesRef.value = [...matchesRef.value]
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tm-live-scores', { detail: daten }))
    }
  }

  function wiederholeGepuffertesFuer(matchesRef) {
    if (eintrag.gepuffertePerMatchId.size === 0) return
    const kuenstlicheNachricht = { scores: [...eintrag.gepuffertePerMatchId.values()].map(oddsMatchScores => ({ oddsMatchScores })) }
    wendeWsUpdateAuchAufTeamSpieleAn(kuenstlicheNachricht, matchesRef.value)
    matchesRef.value = [...matchesRef.value]
  }

  function starteHeartbeat() {
    stoppeHeartbeat()
    eintrag.heartbeatIntervall = setInterval(() => {
      if (document.hidden) return
      if (eintrag.socket?.readyState !== WebSocket.OPEN) return
      try {
        eintrag.socket.send(JSON.stringify({ type: 'ping' }))
      } catch (e) {
        hartNeuVerbinden('Ping nicht sendbar: ' + e)
        return
      }

      clearTimeout(eintrag.pongTimeout)
      eintrag.pongTimeout = setTimeout(
        () => hartNeuVerbinden(`Kein Pong innerhalb von ${PONG_TIMEOUT} ms`),
        PONG_TIMEOUT
      )
    }, HEARTBEAT_INTERVALL)
  }

  function stoppeHeartbeat() {
    clearInterval(eintrag.heartbeatIntervall)
    clearTimeout(eintrag.pongTimeout)
    eintrag.heartbeatIntervall = null
    eintrag.pongTimeout = null
  }

  function hartNeuVerbinden(grund) {
    //console.warn('Hartes Neuverbinden:', grund)
    stoppeHeartbeat()
    clearTimeout(eintrag.wsRetryTimer)
    const alt = eintrag.socket
    eintrag.socket = null          // ab jetzt ignorieren alle Handler des alten Sockets ihre Ereignisse
    try { alt?.close() } catch {}
    eintrag.wsRetryDelay = 2000
    oeffneSocket()
  }
  
  function pruefeVerbindungBeiReaktivierung(erzwingen = false) {
    if (!eintrag.sollReconnecten) return
    const zustand = eintrag.socket?.readyState
    if (zustand === WebSocket.CONNECTING) return

    const warLange = eintrag.versteckt && Date.now() - eintrag.versteckt > 15000
    eintrag.versteckt = null

    if (zustand === WebSocket.OPEN && !erzwingen && !warLange) return

    hartNeuVerbinden('Reaktiviert')
  }

function registriereReaktivierungsListener() {
    if (eintrag.reaktivierungsListenerRegistriert) return
    eintrag.reaktivierungsListenerRegistriert = true
    document.addEventListener('visibilitychange', () => {

      //alert('sichtbar, versteckt=' + eintrag.versteckt)

      if (document.visibilityState === 'hidden') {
        eintrag.versteckt = Date.now()
      } else {
        const kurz = eintrag.versteckt && (Date.now() - eintrag.versteckt) <= 5000
        eintrag.versteckt = null
        pruefeVerbindungBeiReaktivierung()
        if (!kurz) nachReaktivierung()
      }
    })
    window.addEventListener('pageshow', e => {
      if (e.persisted) { pruefeVerbindungBeiReaktivierung(true); nachReaktivierung() }
    })
    window.addEventListener('online', () => {
      pruefeVerbindungBeiReaktivierung(true)
      nachReaktivierung()

      //alert('Reaktiviert: ' + reaktivierungsCallbacks.size + ' Callbacks')
    })
  }

  function meldeRunde(roundID) {
    const rid = Number(roundID)
    if (!Number.isFinite(rid) || rid <= 0) return     // 0 = Gesamtwertung, keine Runde
    eintrag.rundenGemeldet.clear()                    // nur die aktuell angezeigte Runde
    eintrag.rundenGemeldet.add(rid)
    sendeRunden()
  }

  function sendeAuth() {
    try {
      const roh = localStorage.getItem('__oddsapp__client__')
      if (!roh) return
      const reg = JSON.parse(roh)?.oddsRegistration
      const code = reg?.code
      const code1 = reg?.code1

      //console.log('[auth] sende?', { code: !!code, code1: !!code1, offen: eintrag.socket?.readyState === WebSocket.OPEN })

      if (code && code1 && eintrag.socket?.readyState === WebSocket.OPEN) {
        eintrag.socket.send(JSON.stringify({ type: 'auth', code: String(code), code1: String(code1) }))
      }
    } catch (e) { /* localStorage nicht verfügbar oder kein gültiges JSON */ }
  }

  function sendeRunden() {
    if (eintrag.socket?.readyState !== WebSocket.OPEN) return
    for (const rid of eintrag.rundenGemeldet) {
      eintrag.socket.send(JSON.stringify({ type: 'round', roundID: rid }))
    }
  }

  function ladeSpielerTipps(roundID, registrationID) {
    if (eintrag.socket?.readyState !== WebSocket.OPEN) return
    eintrag.socket.send(JSON.stringify({ type: 'spieler', roundID, registrationID }))
  }

  function verbinden() {
    registriereReaktivierungsListener()
    if (eintrag.verbindungGestartet) return
    eintrag.verbindungGestartet = true
    eintrag.sollReconnecten = true
    oeffneSocket()
  }

  function oeffneSocket() {
    clearTimeout(eintrag.wsRetryTimer)
    const sock = new WebSocket(wsUrl)
    sock.binaryType = 'arraybuffer'
    eintrag.socket = sock

    sock.addEventListener('open', () => {
      if (eintrag.socket !== sock) return
      eintrag.wsStatus.value = 'verbunden'
      eintrag.wsRetryDelay = 2000
      starteHeartbeat()

      features.value = []

      sendeAuth()
      sendeRunden()
    })

    sock.addEventListener('message', async (event) => {
      if (eintrag.socket !== sock) return
      clearTimeout(eintrag.pongTimeout)   // jede Nachricht beweist, dass die Verbindung lebt
      try {
        const text = await entpackeNachricht(event.data)
        const daten = parseWsNachricht(text)

        if (daten?.type === 'pong') return

        if (daten && Array.isArray(daten.rankings)) {
          daten.rankings.sort(vergleicheRang)
        }

        verarbeiteNachricht(daten)
      } catch (e) {
        console.warn('WS-Nachricht konnte nicht verarbeitet werden:', e)
      }
    })

    sock.addEventListener('close', () => {
      if (eintrag.socket !== sock) return   // alter Socket, wurde ersetzt
      stoppeHeartbeat()
      if (!eintrag.sollReconnecten) return
      const sekunden = Math.round(eintrag.wsRetryDelay / 1000)
      eintrag.wsStatus.value = `getrennt – erneuter Versuch in ${sekunden}s`
      clearTimeout(eintrag.wsRetryTimer)
      eintrag.wsRetryTimer = setTimeout(() => {
        if (eintrag.sollReconnecten) oeffneSocket()
      }, eintrag.wsRetryDelay)
      eintrag.wsRetryDelay = Math.min(eintrag.wsRetryDelay * 2, 30000)
    })

    sock.addEventListener('error', () => {
      if (eintrag.socket !== sock) return
      try { sock.close() } catch {}
    })
  }

  function aufraeumen() {
    eintrag.matchesListen.delete(matches)
    if (eintrag.matchesListen.size === 0) {
      eintrag.sollReconnecten = false
      stoppeHeartbeat()
      if (eintrag.socket) eintrag.socket.close()
      geteilterWsCache.delete(wsUrl)
    }
  }

  return {
    wsStatus: eintrag.wsStatus,
    rangliste: eintrag.rangliste,
    verbinden,
    aufraeumen,
    rankingsProRunde,
    aenderungenProRunde,
    rankingGesamt,
    tickerEintraege,
    meineTipps,
    liveSpiele,
    meineSpiele,
    meldeRunde,
    letztesUpdate,
    spielerTipps, 
    ladeSpielerTipps,
    features,
    szenarien,
    szenarienTop,
    wiederholeGepuffertes: () => wiederholeGepuffertesFuer(matches),
    onReaktiviert: (fn) => { reaktivierungsCallbacks.add(fn); return () => reaktivierungsCallbacks.delete(fn) }
  }
}